/**
 * TechStackWindow Component
 *
 * Pre-configured Finder window for tech stack browsing by role.
 * Displays technologies organized by CTO, Developer, Infrastructure, etc.
 */

import { useMemo } from 'react'
import { FinderWindow, CategoryConfig, ContentItem } from './FinderWindow'
import { tech, techByCategory, metas } from '@/generated/content'

interface TechStackWindowProps {
  windowId: string
  initialPosition?: { x: number; y: number }
}

export function TechStackWindow({
  windowId,
  initialPosition,
}: TechStackWindowProps) {
  // Build categories from meta or defaults
  const categories: CategoryConfig[] = useMemo(() => {
    const techMeta = metas.tech
    if (techMeta?.categories) {
      return techMeta.categories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        icon: cat.icon,
        description: cat.description,
      }))
    }

    // Default categories if meta not available
    return [
      { id: 'cto', name: 'CTO / Architecture', icon: '👑', description: 'Strategic technology leadership' },
      { id: 'infrastructure', name: 'Infrastructure', icon: '🖥️', description: 'Cloud, DevOps, platform engineering' },
      { id: 'developer', name: 'Developer', icon: '💻', description: 'Hands-on coding' },
      { id: 'product', name: 'Product Lead', icon: '💡', description: 'Product strategy' },
      { id: 'founder', name: 'Founder / COO', icon: '🚀', description: 'Business operations' },
    ]
  }, [])

  // Build items from tech content
  const allItems: ContentItem[] = useMemo(() => {
    return tech.map((item) => ({
      ...item,
      // Ensure name is set
      name: item.name,
    }))
  }, [])

  // Get items for a specific category
  const getCategoryItems = (categoryId: string): ContentItem[] => {
    const items = techByCategory[categoryId] || []
    return items.map((item) => ({
      ...item,
      name: item.name,
    } as ContentItem))
  }

  return (
    <FinderWindow
      windowId={windowId}
      title="Tech Stack"
      icon="⚡"
      initialPosition={initialPosition}
      categories={categories}
      items={allItems}
      getCategoryItems={getCategoryItems}
      defaultView="icon"
      showSidebar={true}
      showPreview={false}
      storageKey="tech-stack-finder"
    />
  )
}

export default TechStackWindow
